// Adapted from pingdotgg/t3code (MIT), packages/client-runtime/src/state/composerDispatch.ts at 7202ba666.
// Upstream's follow-up actions are queue, steer and restart; Amoreg's are the
// `followUpBehavior` client setting's steer, interrupt and queue (interrupt is
// upstream's restart: stop the reply, then send). Amoreg has no mobile client,
// so the helper lives beside the web composer rather than in client-runtime.
import type { FollowUpBehavior } from "@amoreg/contracts";

/** What a message sent during a running reply does to it. */
export type ActiveTurnComposerAction = FollowUpBehavior;

/**
 * How a composer submission is delivered. `auto` starts an ordinary turn: no
 * reply is running, so there is nothing to steer, interrupt or queue behind.
 */
export type ComposerDispatchMode = "auto" | ActiveTurnComposerAction;

/**
 * The alternate (Ctrl/⌘-click, or the `composer.sendAlternate` shortcut)
 * switches between queue and the immediate actions: it queues when the default
 * sends now (steer or interrupt) and steers when the default queues.
 */
export function resolveComposerDispatchMode(input: {
  /** A reply is running, so the follow-up has to steer, interrupt or queue. */
  readonly running: boolean;
  readonly alternateModifier: boolean;
  readonly activeTurnDefault?: ActiveTurnComposerAction | undefined;
}): ComposerDispatchMode {
  if (!input.running) return "auto";
  const defaultAction = input.activeTurnDefault ?? "steer";
  if (input.alternateModifier) return defaultAction === "queue" ? "steer" : "queue";
  return defaultAction;
}

/** What the alternate would do, for labelling the affordance that triggers it. */
export function alternateComposerDispatchAction(
  activeTurnDefault?: ActiveTurnComposerAction,
): ActiveTurnComposerAction {
  return resolveComposerDispatchMode({
    running: true,
    alternateModifier: true,
    activeTurnDefault,
  }) as ActiveTurnComposerAction;
}

/**
 * Words for each follow-up action, shared by the send button, its tooltip and
 * the Settings row so the three always agree.
 */
export const COMPOSER_DISPATCH_ACTION_COPY: Readonly<
  Record<
    ActiveTurnComposerAction,
    {
      /** Option name in Settings. */
      readonly label: string;
      /** The send button's accessible name while this action is armed. */
      readonly ariaLabel: string;
      /** Completes "Click to …" in the send button's tooltip. */
      readonly verb: string;
      /** One-line explanation for the Settings option. */
      readonly description: string;
    }
  >
> = {
  steer: {
    label: "Steer",
    ariaLabel: "Steer message",
    verb: "steer",
    description: "Send now — the reply carries on with your guidance",
  },
  interrupt: {
    label: "Interrupt",
    ariaLabel: "Interrupt and send message",
    verb: "interrupt and send",
    description: "Stop the reply, then send",
  },
  queue: {
    label: "Queue",
    ariaLabel: "Queue message",
    verb: "queue",
    description: "Send after the reply finishes",
  },
};

/** Settings order: the immediate actions first, then queue. */
export const ACTIVE_TURN_COMPOSER_ACTIONS: ReadonlyArray<ActiveTurnComposerAction> = [
  "steer",
  "interrupt",
  "queue",
];

/**
 * The send button's tooltip while a reply runs, e.g. "Click to steer,
 * Ctrl/⌘-click or ⌘Enter to queue".
 */
export function formatComposerDispatchTooltip(input: {
  readonly activeTurnDefault: ActiveTurnComposerAction;
  readonly alternateShortcutLabel: string | null;
}): string {
  const primary = COMPOSER_DISPATCH_ACTION_COPY[input.activeTurnDefault].verb;
  const alternate =
    COMPOSER_DISPATCH_ACTION_COPY[alternateComposerDispatchAction(input.activeTurnDefault)].verb;
  const shortcut = input.alternateShortcutLabel ? ` or ${input.alternateShortcutLabel}` : "";
  return `Click to ${primary}, Ctrl/⌘-click${shortcut} to ${alternate}`;
}
